/**
 * lib/acting/last-act-as — the ONE fact a derived context cannot carry.
 *
 * ⛔ WHY THIS IS ITS OWN FILE AND NOT PART OF `bearer.ts`.
 *
 * This module SUBSCRIBES to the firm-act store at module scope, on purpose: the
 * fact is recorded from every write, not from whoever happens to look. That is
 * the right design for the fact and the wrong thing to put in `bearer.ts`, which
 * `lib/api/client.ts` imports — and therefore very nearly every module and test
 * in the app. Folding it in there broke ELEVEN suites at once with
 * `TypeError: useFirmActStore.subscribe is not a function`, because a test that
 * only wanted to check which bearer went on the wire suddenly had to mock a
 * store subscription. `bearer.ts` stays side-effect free; this file carries the
 * subscription and is imported only by the two places that need the fact.
 */

import { useFirmActStore, type ActiveFirmAct } from '../firm-act-store';
import type { ActingSubject } from './bearer';

/**
 * ⛔ THE RENEWAL TRAP (survey §6-T12), AND WHY THIS IS NOT PART OF ActingContext.
 *
 * `api()` CLEARS the firm-act store as it throws ACT_AS_EXPIRED. So by the time
 * `use-act-as-renewal.ts` catches that error the store is empty, and a context
 * derived purely from live state correctly reports `posture: 'self'` at the
 * exact moment renewal needs to know which client to re-mint for. That hook
 * holds `lastContext` in a ref for this reason and must keep working.
 *
 * The fix is NOT to make the context lie. A context that reported
 * 'acting-as-client' after the store was cleared would pair a subject with no
 * credential and put the guard and the token back into disagreement, which is
 * the defect this whole object exists to end. So the sticky fact lives beside
 * the context, explicitly named, and is never folded into it.
 *
 * Recorded from a store subscription rather than from a read, so it captures
 * every write no matter who is looking. A rule that depends on somebody
 * remembering to look is the shape that keeps costing this codebase.
 *
 * ⚠ IT IS NOT CLEARED AUTOMATICALLY, AND IT CANNOT BE. Sign-out clears the store
 * through `clearActAsContext()`, and `api()` clears it on expiry, and from a
 * subscription those two are the same event: `clear()`. Forgetting on expiry
 * would delete precisely the fact renewal came for. So the exit must say so out
 * loud by calling `forgetLastKnownActAsSubject()`, and the tranche that migrates
 * `lib/act-as-context.ts` is where that call belongs.
 */
let lastActAsSeen: ActiveFirmAct | null = null;

function rememberFirmAct(active: ActiveFirmAct | null): void {
  if (active) lastActAsSeen = active;
}

// Seed from whatever the store already holds (a sessionStorage rehydrate can
// land before this module is first imported), then follow every write.
rememberFirmAct(useFirmActStore.getState().active);
useFirmActStore.subscribe((state) => rememberFirmAct(state.active));

/**
 * The client this session was last administering, whether or not the context is
 * still live. Null when this session has never administered a client, which is
 * the honest answer for an individual onboarding and the signal renewal uses to
 * stay out of the way.
 */
export function lastKnownActAsSubject(): ActingSubject | null {
  if (!lastActAsSeen) return null;
  return {
    tenantId: lastActAsSeen.clientTenantId,
    displayName: lastActAsSeen.clientDisplay,
    kind: 'client',
  };
}

/** Forget the sticky subject. Belongs on the deliberate exits (sign-out,
 *  entering the firm console), NEVER on an expiry. */
export function forgetLastKnownActAsSubject(): void {
  lastActAsSeen = null;
}
